import { useCallback, useRef, useState } from 'react';

import { useAPI } from '../../../../../services/api.service';
import { useDebounce } from '../../../../../hooks/use-debounce';
import { usePaymentState } from '../../../hooks/use-payment-state';

export function useAddressSuggestions() {
  const zipCode = usePaymentState((state) => state.zipCode);
  const fullAddress = usePaymentState((state) => state.fullAddress);
  const [suggestions, setSuggestions] = useState<any[]>(
    fullAddress ? [{ address: fullAddress, zipCode }] : []
  );
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController>();
  const [isAddressSuggestionsNotAvailable, setIsAddressSuggestionsNotAvailable] = useState(false);
  const { API } = useAPI();

  const fetchAddressSuggestions = useCallback(async (searchTerm: string) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      const addressSuggestionsResponse = await API.getAddressSuggestions(
        searchTerm,
        abortControllerRef.current.signal
      );
      if (addressSuggestionsResponse.isValid) {
        setSuggestions(addressSuggestionsResponse.addressResults);
      } else {
        setSuggestions([]);
      }
    } catch (error: any) {
      if (error.message !== 'canceled') {
        setIsLoading(false);
        setIsAddressSuggestionsNotAvailable(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedFunction = useDebounce(fetchAddressSuggestions, 300);

  const removeSuggestions = () => setSuggestions([]);

  return {
    isLoading,
    suggestions,
    removeSuggestions,
    isAddressSuggestionsNotAvailable,
    fetchAddressSuggestions: debouncedFunction,
  };
}
